import React, { useState } from 'react';
import { Button, Input, Textarea, Checkbox } from '@heroui/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@heroui/drawer';
import { sendOrderEmail } from '../apiEndpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import useIsMobile from '../hooks/useIsMobile';
import { btnAccent, btnGhost, label as labelCls } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';

interface BulkEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    orders: any[];
    eventTitle?: string;
}

export default function BulkEmailModal({ isOpen, onClose, orders, eventTitle }: BulkEmailModalProps) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [totalProgress, setTotalProgress] = useState(0);
    const [search, setSearch] = useState('');
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    const sendEmailMutation = useMutation({
        mutationFn: async (data: { orderId: string; subject: string; message: string }) =>
            sendOrderEmail(data.orderId, { subject: data.subject, body: data.message }),
        onSuccess: () => {
            setCurrentProgress(prev => prev + 1);
        },
        onError: (error) => {
            console.error('Failed to send email:', error);
        }
    });

    const handleClose = () => {
        setSubject('');
        setMessage('');
        setSelectedOrders([]);
        setIsLoading(false);
        setCurrentProgress(0);
        setTotalProgress(0);
        setSearch('');
        onClose();
    };

    const handleOrderToggle = (orderId: string) => {
        setSelectedOrders(prev =>
            prev.includes(orderId)
                ? prev.filter(id => id !== orderId)
                : [...prev, orderId]
        );
    };

    const handleSendBulkEmail = async () => {
        if (!subject.trim() || !message.trim() || selectedOrders.length === 0) {
            return;
        }

        setIsLoading(true);
        setTotalProgress(selectedOrders.length);
        setCurrentProgress(0);

        try {
            // Send emails sequentially to avoid overwhelming the server
            for (const orderId of selectedOrders) {
                await sendEmailMutation.mutateAsync({
                    orderId,
                    subject: subject.trim(),
                    message: message.trim()
                });
            }

            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['eventOrders'] });
            const sentCount = selectedOrders.length;
            addToast({
                title: 'Email sent',
                description: `Email sent to ${sentCount} ${sentCount === 1 ? 'recipient' : 'recipients'}.`,
                color: 'success',
            });
            handleClose();
        } catch (error: any) {
            console.error('Bulk email failed:', error);
            addToast({
                title: 'Email failed',
                description: error?.response?.data?.message || error?.message || 'Some emails could not be sent. Please try again.',
                color: 'danger',
            });
            setIsLoading(false);
        }
    };

    const eligibleOrders = orders;

    const getName = (order: any) => order.customerName || order.guestName || order.attendeeName || 'N/A';
    const getEmail = (order: any) => order.customerEmail || order.guestEmail || order.email || 'N/A';

    const query = search.trim().toLowerCase();
    const visibleOrders = query
        ? eligibleOrders.filter(order =>
            [getName(order), getEmail(order), order.hashedUniqueId, order.orderId]
                .some(v => String(v || '').toLowerCase().includes(query)))
        : eligibleOrders;

    const allVisibleSelected = visibleOrders.length > 0 && visibleOrders.every(o => selectedOrders.includes(o._id));

    const toggleVisible = () => {
        const ids = visibleOrders.map(o => o._id);
        setSelectedOrders(prev => allVisibleSelected
            ? prev.filter(id => !ids.includes(id))
            : Array.from(new Set([...prev, ...ids])));
    };

    const statusClass = (status: string) =>
        status === 'completed' ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400' :
            status === 'checked-in' ? 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent' :
                status === 'refunded' ? 'bg-error95 text-error40 dark:bg-error50/15 dark:text-error70' :
                    status === 'disputed' ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300' :
                        'bg-grey95 text-grey30 dark:bg-white/10 dark:text-nightTextSecondary';

    const sectionTitle = 'text-xs font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted';
    const hint = 'text-sm text-grey40 dark:text-nightMuted';
    const progressPct = totalProgress > 0 ? (currentProgress / totalProgress) * 100 : 0;

    return (
        <Drawer
            placement="right"
            hideCloseButton
            isOpen={isOpen}
            onClose={handleClose}
            isDismissable={!isLoading}
            size={isMobile ? 'full' : '2xl'}
            scrollBehavior="inside"
            classNames={{
                wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                backdrop: isMobile ? 'z-[100000] bg-black/50' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
            }}
        >
            <DrawerContent>
                <DrawerHeader className="flex items-start justify-between px-6 pt-6 pb-4">
                    <div>
                        <h2 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Send Bulk Email</h2>
                        <p className={`${hint} font-normal mt-2`}>
                            {eventTitle ? `Email attendees of ${eventTitle}` : 'Send an email to multiple customers at once'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isLoading}
                        className="w-10 h-10 flex items-center justify-center rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors flex-shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </DrawerHeader>

                <DrawerBody className="px-6 py-2 gap-6">
                    <div className="flex flex-col gap-4">
                        <p className={sectionTitle}>Compose</p>
                        <div>
                            <label className={labelCls}>Subject</label>
                            <Input
                                aria-label="Subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Subject..."
                                isDisabled={isLoading}
                                classNames={{ inputWrapper: `h-14 ${fieldWrap}`, input: fieldText }}
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className={`${labelCls} mb-0`}>Message</label>
                                <span className="rounded-full bg-grey99 px-2.5 py-1 text-xs font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">{message.length} characters</span>
                            </div>
                            <Textarea
                                aria-label="Message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Message..."
                                minRows={isMobile ? 4 : 6}
                                maxRows={12}
                                isDisabled={isLoading}
                                classNames={{ inputWrapper: fieldWrap, input: fieldText }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <p className={sectionTitle}>Recipients</p>
                            <span className={hint}>{selectedOrders.length} of {eligibleOrders.length} selected</span>
                        </div>

                        <div className="rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] overflow-hidden">
                            <div className="flex items-center gap-3 p-3 pl-4 border-b border-black/[0.06] dark:border-white/[0.06]">
                                <Checkbox
                                    aria-label="Select all"
                                    isSelected={allVisibleSelected}
                                    isIndeterminate={!allVisibleSelected && visibleOrders.some(o => selectedOrders.includes(o._id))}
                                    onValueChange={toggleVisible}
                                    isDisabled={isLoading || visibleOrders.length === 0}
                                    classNames={{ wrapper: 'after:bg-primary40 dark:after:bg-nightAccent' }}
                                />
                                <div className="relative flex-1">
                                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-grey60 dark:text-nightMuted pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                        <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search by name, email or order ID..."
                                        className="w-full h-11 pl-10 pr-4 rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 text-sm text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60"
                                    />
                                </div>
                            </div>

                            <div className="max-h-[360px] overflow-y-auto">
                                {visibleOrders.length === 0 ? (
                                    <div className={`${hint} text-center py-10`}>
                                        {eligibleOrders.length === 0 ? 'No orders to email yet' : 'No orders match your search'}
                                    </div>
                                ) : (
                                    visibleOrders.map((order) => {
                                        const name = getName(order);
                                        const email = getEmail(order);
                                        const isSelected = selectedOrders.includes(order._id);
                                        return (
                                            <div
                                                key={order._id}
                                                onClick={() => !isLoading && handleOrderToggle(order._id)}
                                                className={`flex items-center gap-3 px-4 py-3 border-b border-black/[0.04] dark:border-white/[0.06] last:border-0 cursor-pointer transition-colors ${isSelected ? 'bg-primary/[0.06] dark:bg-nightAccent/10' : 'hover:bg-grey95 dark:hover:bg-white/[0.04]'}`}
                                            >
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        aria-label={`Select ${name}`}
                                                        isSelected={isSelected}
                                                        onValueChange={() => handleOrderToggle(order._id)}
                                                        isDisabled={isLoading}
                                                        classNames={{ wrapper: 'after:bg-primary40 dark:after:bg-nightAccent' }}
                                                    />
                                                </div>
                                                <div className="w-10 h-10 rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/10 dark:ring-0 text-grey10 dark:text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                                                    {String(name).charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-grey10 dark:text-white truncate">{name}</div>
                                                    <div className="text-xs text-grey40 dark:text-nightMuted truncate">{email}</div>
                                                </div>
                                                <div className="hidden sm:block text-xs font-mono text-grey40 dark:text-nightMuted truncate max-w-[120px]">
                                                    {order.hashedUniqueId || order.orderId}
                                                </div>
                                                <span className={`inline-flex items-center h-[24px] px-2.5 rounded-full text-xs font-semibold capitalize flex-shrink-0 ${statusClass(order.status)}`}>
                                                    {order.status}
                                                </span>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>

                    {isLoading && (
                        <div className="rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-4 flex flex-col gap-2">
                            <div className="flex justify-between text-sm">
                                <span className="font-semibold text-grey10 dark:text-white">Sending emails...</span>
                                <span className="text-grey40 dark:text-nightMuted">{currentProgress} / {totalProgress}</span>
                            </div>
                            <div className="w-full bg-grey90 dark:bg-white/10 rounded-full h-2">
                                <div
                                    className="bg-primary dark:bg-nightAccent h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                        </div>
                    )}
                </DrawerBody>

                <DrawerFooter className="px-6 pt-4 pb-6 border-t border-black/[0.06] dark:border-white/[0.06]">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
                        <span className={hint}>
                            {selectedOrders.length} recipient{selectedOrders.length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                variant="light"
                                onPress={handleClose}
                                isDisabled={isLoading}
                                className={`${btnGhost} flex-1 sm:flex-none bg-transparent`}
                            >
                                Cancel
                            </Button>
                            <Button
                                onPress={handleSendBulkEmail}
                                isLoading={isLoading}
                                isDisabled={!subject.trim() || !message.trim() || selectedOrders.length === 0}
                                className={`${btnAccent} flex-1 sm:flex-none`}
                            >
                                {isLoading ? `Sending (${currentProgress}/${totalProgress})` : `Send Email (${selectedOrders.length})`}
                            </Button>
                        </div>
                    </div>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
